import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Button,
  styled,
  useTheme
} from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import QuestionSplit from './components/question_split';
import LeftCardView from './components/LeftCardView';
import RightReadingView from './components/RightReadingView';
import CharacterIntro from './components/CharacterIntro';
import FeedbackView from './components/FeedbackView';
import RightQuestion from './components/RightQuestion';

import { saveDetailNPCReading, getCurrentUser } from './services/userReadingService';
import { imageSetForPNG } from './utils/imageset';
import { languages, useLanguage, translateFramework, translateFortuneQuestion } from './data/languages.js';

const DetailView = styled(Box)(({ theme }) => ({
  position: 'fixed',
  height: '100vh',
  width: '100vw',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 10,
  display: 'flex',
  flexDirection: 'row',
  overflow: 'hidden',
}));

const LeftSection = styled(Box)(({ theme }) => ({
  width: '50%',
  height: '100vh',
  position: 'relative',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  overflow: 'auto',
}));

const RightSection = styled(Box)(({ theme }) => ({
  width: '50%',
  backgroundColor: 'rgba(255, 255, 255)',
  // padding: theme.spacing(8),
  height: '100vh',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
}));

const LoadingView = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
}));


const AnimatedBackground = styled(Box)(({ colors }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundImage: colors ? 
    colors.map((color, index) => 
      `radial-gradient(closest-side, ${color}, ${color}00)`
    ).join(',') :
    `
      radial-gradient(closest-side, #ece5d4, #ece5d400),
      radial-gradient(closest-side, #e6cda8, #e6cda800),
      radial-gradient(closest-side, #8dc1c9, #8dc1c900)
    `,
  backgroundSize: colors ?
    colors.map(() => '130vmax 130vmax').join(',') :
    '130vmax 130vmax, 130vmax 130vmax, 130vmax 130vmax',
  backgroundPosition: colors ?
    colors.map((_, index) => `${-30 + (index * 20)}vmax ${-30 + (index * 20)}vmax`).join(',') :
    '-60vmax -60vmax, 0vmax 0vmax, 60vmax 60vmax',
  backgroundRepeat: 'no-repeat',
  animation: 'movement 20s linear infinite',
  zIndex: -1,
  transition: 'all 0.5s ease-in-out',
  '&::after': {
    content: '""',
    position: 'absolute',
    width: '100%',
    height: '100%',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
  },
  '@keyframes movement': {
    '0%, 100%': {
      backgroundSize: '130vmax 130vmax, 80vmax 80vmax, 90vmax 90vmax, 110vmax 110vmax, 90vmax 90vmax',
      backgroundPosition: '-80vmax -80vmax, 60vmax -30vmax, 10vmax 10vmax, -30vmax -10vmax, 50vmax 50vmax',
    },
    '25%': {
      backgroundSize: '100vmax 100vmax, 90vmax 90vmax, 100vmax 100vmax, 90vmax 90vmax, 60vmax 60vmax',
      backgroundPosition: '-60vmax -90vmax, 50vmax -40vmax, 0vmax -20vmax, -40vmax -20vmax, 40vmax 60vmax',
    },
    '50%': {
      backgroundSize: '80vmax 80vmax, 110vmax 110vmax, 80vmax 80vmax, 60vmax 60vmax, 80vmax 80vmax',
      backgroundPosition: '-50vmax -70vmax, 40vmax -30vmax, 10vmax 0vmax, 20vmax 10vmax, 30vmax 70vmax',
    },
    '75%': {
      backgroundSize: '90vmax 90vmax, 90vmax 90vmax, 100vmax 100vmax, 90vmax 90vmax, 70vmax 70vmax',
      backgroundPosition: '-50vmax -40vmax, 50vmax -30vmax, 20vmax 0vmax, -10vmax 10vmax, 40vmax 60vmax',
    },
  },
}));

export default function DetailNPC_daily({ onSelectNPC, onCardDraw }) {
  const [searchParams] = useSearchParams();
  const urlLang = searchParams.get('language') || 'en'; // URL语言参数
  const currentLang = useLanguage() || urlLang; // 响应式语言配置，URL优先
  const config = useMemo(() => languages[currentLang], [currentLang]); // 优化语言配置获取

  const [viewState, setViewState] = useState('question');
  const [cardData, setCardData] = useState(null);
  const [cardImages, setCardImages] = useState({});
  const [answer, setAnswer] = useState('');
  const [cardIndex, setCardIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isScrollingToIndex, setIsScrollingToIndex] = useState(false);
  const [history, setHistory] = useState([]);
  const [followUpHistory, setFollowUpHistory] = useState([]);
  const [followUpAnswers, setFollowUpAnswers] = useState([]);
  const [currentView, setCurrentView] = useState('reading');
  const [feedbackRating, setFeedbackRating] = useState(null);
  const [initialStressLevel, setInitialStressLevel] = useState(null);
  const [finalStressLevel, setFinalStressLevel] = useState(null);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [loadingStartTime, setLoadingStartTime] = useState(null);
  const loadingMessages = config.detailNPC.loadingMessages;

  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { character, readingData, fromHistory } = location.state || {};
  
  // Always set isDailyFortune to true for this component
  const isDailyFortune = true;

  // Calculate total sections (cards + summary)
  const totalSections = (cardData?.cards?.split(', ').length + 1 + followUpAnswers.length) || 1;
  console.log("totalSections", totalSections);

  // Get reading data if coming from history
  const { readingData: historyReadingData, fromHistory: fromHistoryReading } = location.state || {};
  
  // Initialize state with reading data if available
  useEffect(() => {
    if (fromHistoryReading && historyReadingData) {
      setCardData({
        cards: historyReadingData.cards,
        selectedQuestion: historyReadingData.question,
        framework: historyReadingData.framework,
      });
      setAnswer(historyReadingData.answer);
      setHistory(historyReadingData.history || []);
      setInitialStressLevel(historyReadingData.initialStressLevel);
      setViewState('reading');
    }
  }, [fromHistoryReading, historyReadingData]);

  useEffect(() => {
    if (cardData) {
      loadCardImages();
    }
  }, [cardData]);

  const loadCardImages = async () => {
    if (!cardData?.cards) return; // 检查 cardData.cards 是否存在

    const newImages = {
      'Card Back': '/card_back.jpg'
    };
    
    const cards = cardData.cards.split(', ');
    for (const card of cards) {
      try {
        const response = await fetch(`/cards/${card}`);
        if (response.ok) {
          const blob = await response.blob();
          newImages[card] = URL.createObjectURL(blob);
        }
      } catch (error) {
        console.error('Error loading card image:', error);
      }
    }

    setCardImages(newImages);
  };

  const handleInitialStressChange = (level) => {
    setInitialStressLevel(level);
    console.log(`Initial stress level set to: ${level}/10`);
  };

  const handleFinalStressChange = (rating) => {
    setFinalStressLevel(rating);
    console.log(`Final stress level set to: ${rating}/10`);
  };

  const handleCardDraw = async (data) => {
    setCardData({
      ...data,
      initialStressLevel: initialStressLevel
    });
    setIsLoading(true);
    setLoadingMessageIndex(0); // 重置加载消息索引
    setLoadingStartTime(Date.now()); // 记录开始时间
    
    try {
      // 先发起API请求
      const response = await fetch('/answer?' + new URLSearchParams({
        framework: data.framework,
        question: data.selectedQuestion,
        cardone: data.cardOne,
        cardtwo: data.cardTwo,
        cardthree: data.cardThree,
      }));
      
      if (response.ok) {
        const responseData = await response.json();
        
        // 计算已经过去的时间
        const elapsedTime = Date.now() - loadingStartTime;
        const remainingTime = Math.max(0, 9000 - elapsedTime);
        
        // 等待剩余时间
        await new Promise(resolve => setTimeout(resolve, remainingTime));
        
        setAnswer(responseData.answer);
        setHistory(responseData.history || []);
        setFollowUpHistory([]);

        // Save reading with complete data
        saveDetailNPCReading({
          cards: data.cards,
          question: data.selectedQuestion,
          framework: data.framework,
          answer: responseData.answer,
          initialStressLevel,
          character: character?.id || null,
          history: responseData.history || []
        });
        
        setViewState('reading');
        setCardIndex(0);
      } else {
        console.error('Error getting answer:', response.statusText);
      }
    } catch (error) {
      console.error('Error getting answer:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueAsk = async (followUpQuestion, newFollowUpHistory) => {
    setIsLoading(true);
    
    // Update the follow-up history state with the new history
    setFollowUpHistory(newFollowUpHistory);
    
    // Add the new follow-up to the list if needed for other views
    const lastAnswer = newFollowUpHistory[newFollowUpHistory.length - 1]?.content;
    if (lastAnswer) {
      setFollowUpAnswers(prev => [...prev, {
        question: followUpQuestion,
        answer: lastAnswer
      }]);
    }
    
    // Keep the view state as 'followup' to stay in RightQuestion component
    setViewState('followup');
    setCurrentView('followup');
    
    setIsLoading(false);
    
    // Save the full history (context + follow-up) for persistence
    const fullHistory = [...history, ...newFollowUpHistory];
    if (fromHistory && readingData) {
      const updatedReading = {
        ...readingData,
        history: fullHistory,
      };
      saveDetailNPCReading(updatedReading);
    }
  };

  const handleScroll = useCallback(({ direction }) => {
    if (isScrollingToIndex) return;
    
    const newIndex = cardIndex + direction;
    if (newIndex >= 0 && newIndex < totalSections) {
      setCardIndex(newIndex);
      setIsScrollingToIndex(true);

      console.log(cardIndex);
      
      // Reset scrolling flag after animation
      setTimeout(() => {
        setIsScrollingToIndex(false);
      }, 500);
    }
  }, [cardIndex, totalSections, isScrollingToIndex]); 

  

  const handleCardClick = (index) => {
    setCardIndex(index);
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
    
    // If switching to reading view, reset to the appropriate card index
    if (view === 'reading') {
      // If we were on a follow-up, go to the summary
      const cardCount = cardData?.cards?.split(', ').length || 0;
      setCardIndex(cardCount);
    } else if (view === 'followup' && followUpAnswers.length > 0) {
      // If switching to follow-up view, show the first follow-up
      const cardCount = cardData?.cards?.split(', ').length || 0;
      setCardIndex(cardCount + 1);
    }
  };

  // Add a function to handle scrolling between follow-up questions
  const handleFollowUpScroll = ({ direction }) => {
    if (isScrollingToIndex) return;
    
    const cardCount = cardData?.cards?.split(', ').length || 0;
    const currentFollowUpIndex = cardIndex - (cardCount + 1);
    const newFollowUpIndex = currentFollowUpIndex + direction;
    
    // Check if the new index is within bounds
    if (newFollowUpIndex >= 0 && newFollowUpIndex < followUpAnswers.length) {
      const newCardIndex = cardCount + 1 + newFollowUpIndex;
      setCardIndex(newCardIndex);
      setIsScrollingToIndex(true);
      
      // Reset scrolling flag after animation
      setTimeout(() => {
        setIsScrollingToIndex(false);
      }, 500);
    }
  };

  const handleFeedbackChange = (rating) => {
    setFeedbackRating(rating);
    setFinalStressLevel(rating);
    
    if (cardData) {
      setCardData({
        ...cardData,
        finalStressLevel: rating
      });
    }
    
    console.log(`User rated their experience: ${rating}/10`);
  };

  // Add a dedicated function to handle fortune fetching and rendering
  const handleFortuneFetch = (type) => {
    console.log(`Selected ${type} fortune reading`);
    setIsLoading(true);
    
    // Generate a generic question based on the type (English for API, translated for display)
    const englishQuestion = type === 'daily'
      ? "What does the universe have in store for me today?"
      : "What should I focus on this week?";
    const translatedQuestion = translateFortuneQuestion(type, currentLang);

    // First, draw a single card
    fetch(`/draw?count=1`)
      .then(response => response.json())
      .then(data => {
        console.log("Card drawn:", data);

        // Now send the fortune request with the card data (use English question for backend)
        // Use character.id instead of character.name to match the new endpoint
        const user = getCurrentUser();
        return fetch(`/fortune?type=${type}&cards=${data.cards}&character=${character?.id || '1'}&language=${currentLang}&user_id=${user?.id || ''}`);
      })
      .then(response => response.json())
      .then(data => {
        console.log("Fortune response:", data);

        // Set card data (use translated question for display)
        setCardData({
          selectedQuestion: translatedQuestion,
          framework: translateFramework(type === 'daily' ? "Daily Fortune" : "Weekly Guidance", currentLang),
          cards: data.cards,
          cardOne: data.cardone, // Make sure to use the cardone from the response
          cardCount: 1,
          initialStressLevel: initialStressLevel
        });

        // Set answer and history
        setAnswer(data.answer);
        setHistory(data.history || []);

        // Save reading (use translated question for saving)
        saveDetailNPCReading({
          cards: data.cards,
          question: translatedQuestion,
          framework: translateFramework(type === 'daily' ? "Daily Fortune" : "Weekly Guidance", currentLang),
          answer: data.answer,
          initialStressLevel,
          character: character?.id || '1', // Use character.id consistently
          history: data.history || []
        });
        
        // Update view state
        // setViewState('reading');
        setCardIndex(0);
        // setIsLoading(false);
      })
      .catch(error => {
        console.error("Error in fortune reading:", error);
        setIsLoading(false);
      });
  };

  const handleAskFollowUp = () => {
    setViewState('followup');
    setCurrentView('followup');
  };

  // Add effect to cycle through loading messages
  useEffect(() => {
    if (isLoading) {
      setLoadingMessageIndex(0);
      
      // 每条消息显示3秒
      const interval = setInterval(() => {
        setLoadingMessageIndex(prev => {
          const nextIndex = prev + 1;
          // 如果到达最后一条消息，保持在最后一条
          if (nextIndex >= loadingMessages.length) {
            clearInterval(interval);
            return prev;
          }
          return nextIndex;
        });
      }, 3000);

      // 设置一个9秒的总时间（3条消息 * 3秒）
      const timeout = setTimeout(() => {
        clearInterval(interval);
        setIsLoading(false);
        setViewState('reading');
      }, 9000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
        setLoadingMessageIndex(0);
      };
    }
  }, [isLoading]);

  const renderLeftSection = () => {
    if (viewState === 'reading' && cardData && answer && !isLoading) {
      return (
        <LeftCardView 
          cardData={cardData}
          cardImages={cardImages}
          onCardClick={handleCardClick}
          index={cardIndex}
          character={character}
          onContinueAsk={handleContinueAsk}
          followUpCount={followUpAnswers.length}
          onViewChange={handleViewChange}
          currentView={currentView}
        />
      );
    }
    return null;
  };

  const renderRightSection = () => {
    if (isLoading) {
      return (
        <LoadingView>
          <Typography 
            variant="h5" 
            sx={{ 
              fontFamily: "'Space Grotesk', sans-serif",
              textAlign: 'center',
              maxWidth: '600px',
              lineHeight: 1.5,
              whiteSpace: 'pre-line',
              fontWeight: 500,
              color: 'rgba(0, 0, 0, 0.8)',
              letterSpacing: '0.5px',
              padding: '0 24px'
            }}
          >
            {loadingMessages[loadingMessageIndex]}
          </Typography>
        </LoadingView>
      );
    }

    switch (viewState) {
      case 'question':
        console.log("inside daily", character);
        return (
          <QuestionSplit 
            selectedNPC={character}
            onCardDraw={handleCardDraw}
            onFortuneSelect={handleFortuneFetch}
            onInitialChange={handleInitialStressChange}
            isDailyFortune={true}
          />
        );
      case 'reading':
        // For fortune readings (single card), show everything on one page
        const frameworkValue = Array.isArray(cardData?.framework)
          ? cardData.framework[0]
          : cardData?.framework;
        if (frameworkValue === "Daily Fortune" || frameworkValue === "Weekly Guidance" ||
            frameworkValue === "每日运势" || frameworkValue === "每周指引") {
          return (
            <RightReadingView
              index={0}
              content={answer}
              title={frameworkValue || "Fortune Reading"}
              onScroll={() => {}} // No scrolling needed
              history={history}
              isFollowUp={false}
              question={cardData?.selectedQuestion}
              isFortuneReading={true}
              onAskFollowUp={handleAskFollowUp}
            />
          );
        }
        
        // For regular readings, use the existing logic
        const sections = answer?.split('\n\n') || [];
        const cardCount = cardData?.cards?.split(', ').length || 0;
        
        // If we're in follow-up view, show the follow-up content
        if (currentView === 'followup') {
          const followUpIndex = cardIndex - (cardCount + 1);
          // If the index is valid, show the follow-up
          if (followUpIndex >= 0 && followUpIndex < followUpAnswers.length) {
            const followUp = followUpAnswers[followUpIndex];
            return (
              <RightReadingView
                index={followUpIndex}
                content={followUp.answer}
                title={`Follow-up: ${followUp.question}`}
                onScroll={handleFollowUpScroll}
                history={history}
                isFollowUp={true}
                totalItems={followUpAnswers.length}
                question={cardData?.selectedQuestion}
              />
            );
          }
          // If the index is invalid, default to the first follow-up
          else if (followUpAnswers.length > 0) {
            const followUp = followUpAnswers[0];
            setCardIndex(cardCount + 1); // Set to first follow-up
            return (
              <RightReadingView
                index={cardCount + 1}
                content={followUp.answer}
                title={`Follow-up: ${followUp.question}`}
                onScroll={handleScroll}
                history={history}
                isFollowUp={true}
                question={cardData?.selectedQuestion}
                onFeedbackChange={handleFeedbackChange}
              />
            );
          }
        }
        // Add a dedicated feedback page after the summary
        else if (cardIndex === cardCount + 1) {
          return (
            <Box sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 4
            }}>
              <Typography variant="h4" sx={{ 
                fontFamily: "'Recoleta', serif",
                mb: 4,
                textAlign: 'center'
              }}>
                How Do You Feel?
              </Typography>
              
              <FeedbackView 
                question={cardData?.selectedQuestion}
                onRatingChange={handleFinalStressChange}
                initialStressLevel={initialStressLevel}
                finalStressLevel={finalStressLevel}
              />
            </Box>
          );
        }
        // Otherwise, show the regular reading content
        else if (cardIndex > cardCount + 1) {
          // If we're beyond the feedback page, go back to summary
          setCardIndex(cardCount);
          const content = sections[sections.length - 1];
          return (
            <RightReadingView 
              index={cardCount}
              content={content}
              title="Reading Summary"
              onScroll={handleScroll}
              history={history}
              isFollowUp={false}
              question={cardData?.selectedQuestion}
              onFeedbackChange={handleFeedbackChange}
            />
          );
        }
        else {
          const isSummary = cardIndex === cardCount;
          const content = isSummary ? sections[sections.length - 1] : sections[cardIndex];
          const title = isSummary ? "Reading Summary" : `Card ${cardIndex + 1} Interpretation`;
          
          return (
            <RightReadingView 
              index={cardIndex}
              content={content}
              title={title}
              onScroll={handleScroll}
              history={history}
              isFollowUp={false}
              question={cardData?.selectedQuestion}
              totalItems={cardCount + 2} // Add +2 to include summary and feedback pages
            />
          );
        }
        break;
      case 'followup':
        return (
          <RightQuestion
            selectedNPC={character}
            onQuestionSubmit={handleContinueAsk}
            context={history}
            history={followUpHistory}
            initialStressLevel={initialStressLevel}
            finalStressLevel={finalStressLevel}
            onFinalStressChange={handleFeedbackChange}
          />
        );
      default:
        return null;
    }
  };

  if (!character && !fromHistory) {
    navigate('/character');
    return null;
  }

  return (
    <DetailView>
      {viewState == 'reading' && <AnimatedBackground colors={character?.colors} />}
      <LeftSection
        sx={{
          backgroundImage: viewState !== 'reading' || !answer ? 
            imageSetForPNG(character?.background) : 'none',
        }}
      >
        {renderLeftSection()}
      </LeftSection>
      <RightSection>
        {renderRightSection()}
      </RightSection>
    </DetailView>
  );
} 